// libs
import { useState, useEffect, useCallback } from 'react';
// contexts
import { useToastContext, TOASTER_TYPES } from 'shared/components/Toast';

// apis
import { notifyMe as notifyUser } from 'shared/api/notify-user';

// helpers
import { validateEmail } from 'shared/helpers/utils';
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';

// constants
import { TEXTS } from 'shared/components/Notify/constants';
import { getEmailInSessionStorage, setEmailInSessionStorage } from '../helpers/emailHelpers';
// defs
interface INotifyApiProps {
  customerEmail: string;
  productId: string;
  onNotified?: VoidFunction;
  showToastMessage?: boolean;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

const useNotifyApi = ({
  customerEmail,
  productId,
  onNotified = EMPTY_FUNC,
  showToastMessage = true,
}: INotifyApiProps) => {
  const [isErrorInEmail, setIsErrorInEmail] = useState(false);
  const [isApiSuccess, setApiSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [apiFailure, setApiFailure] = useState<boolean>(false);
  const { showToast } = useToastContext();
  const isMobile = getDeviceType() === DEVICE_TYPE.MOBILE;

  useEffect(() => {
    if (customerEmail.length > 0) {
      setUserEmail(customerEmail);
      return;
    }

    const email = getEmailInSessionStorage();
    if (email && email.length > 0) {
      setUserEmail(email);
      return;
    }
  }, [customerEmail, setUserEmail]);

  const handleToast = (type: TOASTER_TYPES, message: string, statusCode: string) => {
    showToast({ type, message, statusCode });
  };

  const reset = useCallback(() => {
    setApiSuccess(false);
    setIsErrorInEmail(false);
    setApiFailure(false);
  }, []);

  useEffect(() => {
    reset();
  }, [productId, reset]);

  const onNotifyClick = async () => {
    if (isApiSuccess) {
      return;
    }

    const isEmailValid = validateEmail(userEmail);
    if (isEmailValid) {
      setIsErrorInEmail(false);

      setInProgress(true);
      return notifyUser(userEmail, productId)
        .then(() => {
          setApiSuccess(true);
          setEmailInSessionStorage(userEmail);
          onNotified();
        })
        .catch((err) => {
          if (showToastMessage || isMobile)
            handleToast(TOASTER_TYPES.ERROR, TEXTS.NOTIFY_ERROR, err?.statusCode);
          setApiFailure(true);
        })
        .then(() => {
          setInProgress(false);
        });
    } else {
      setIsErrorInEmail(true);
    }
  };

  return {
    setUserEmail,
    onNotifyClick,
    userEmail,
    isErrorInEmail,
    isApiSuccess,
    inProgress,
    validEmail: validateEmail(userEmail),
    apiFailure,
    reset,
  };
};

export default useNotifyApi;
